import { useEffect, useMemo, useState } from 'react'
import { useAppSelector } from 'redux/hooks'
import { fleetApi } from 'api'
import { useNavigate, useParams } from 'react-router-dom'
import { FullCompanyEquipmentDto, PaymentPeriod } from 'api/fleet/types'
import { FormValues } from '../EquipmentDetailsForm/types'
import {
  EQUIPMENT_DETAILS,
  EQUIPMENT_DETAILS_BREADCRUMB,
  EQUIPMENT_TITLE,
} from 'texts/equipmentDetails'
import { EquipmentDetailsForm } from '../EquipmentDetailsForm'
import { Container } from 'ui/molecules/Container'
import {
  isMigratedUserSelector,
  isOperatorSelector,
} from 'redux/login/selectors'
import { getDetailsBreadcrumbsList } from 'utils/getDetailsBreadcrumbsList'
import { useApiRequest } from 'hooks/useApiRequest'
import { setNotificationMessage } from 'redux/notifications/slice'
import { useDispatch } from 'react-redux'
import { useCompanyId } from 'hooks/useCompanyId'
import { useGetEquipmentDetails } from '../../hooks/useGetEquipmentDetails'
import { locations } from 'utils/locations'
import { URLS } from 'constants/urls'
import { EQUIPMENT_FORM_ID } from '../../constants'
import { FormActionButtons } from 'ui/components/FormActionButtons'
import { useMigrationRedirect } from 'hooks/useMigrationRedirect'

export const EquipmentDetails = () => {
  const [equipmentInfo, setEquipmentInfo] = useState<FullCompanyEquipmentDto>(
    {}
  )
  const { id } = useParams()

  const isOperator = useAppSelector(isOperatorSelector)
  const isMigratedUser = useAppSelector(isMigratedUserSelector)
  const isMigratedUserFlow = isMigratedUser && !isOperator
  const updateEquipmentRequest = useApiRequest(
    (values) => fleetApi.updateEquipment(id || '', values),
    undefined,
    undefined,
    undefined,
    false
  )
  const getEquipmentInfoRequest = useApiRequest((id) =>
    fleetApi.getEquipmentInfo(id)
  )

  const { redirect } = useMigrationRedirect()

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { equipmentDetailsData, isLoaded } = useGetEquipmentDetails()

  const companyId = useCompanyId()

  const breadcrumbList = getDetailsBreadcrumbsList(
    isOperator,
    String(companyId),
    'equipments'
  )

  useEffect(() => {
    const getEquipmentDetails = async () => {
      if (id) {
        const response = await getEquipmentInfoRequest.apiRequest(id)
        if (response) {
          setEquipmentInfo(response.data.extraInfo)
        }
      }
    }
    getEquipmentDetails()
  }, [id])

  const handleSubmit = async (data: Partial<FormValues>) => {
    const values = {
      ...data,
      extraId: Number(data.extraId),
      price: Number(data.price),
      companyId: Number(companyId),
      maxAmount: Number(data.maxAmount),
      hirePoints: data.hirePoints?.map((item) => Number(item.value)),
      priceFor: Number(data.priceFor),
    }

    const response = await updateEquipmentRequest.apiRequest(values)
    if (response) {
      dispatch(
        setNotificationMessage({
          notificationMessage: EQUIPMENT_DETAILS.SUCCESS_EDIT,
        })
      )
      if (isMigratedUserFlow) {
        redirect(companyId, 'equipmentStatus')
      }
      navigate(locations.equipments(isOperator, companyId))
    }
  }

  const initValues = useMemo(
    () => ({
      hirePoints: equipmentInfo.hirePoints
        ? equipmentInfo.hirePoints.map((item) => ({
            value: `${item.id}`,
            label: `${item.name}`,
          }))
        : [],
      price: `${equipmentInfo.price}`,
      maxAmount:
        Number(equipmentInfo.priceFor) === PaymentPeriod.Rent
          ? ''
          : `${equipmentInfo.maxAmount}`,
      extraId: String(equipmentInfo.extraId) || '',
      priceFor: String(equipmentInfo.priceFor) || '',
      isRequired: equipmentInfo.isRequired,
    }),
    [equipmentInfo]
  )

  const handleCancel = () =>
    navigate(`${URLS.FLEET}${URLS.EQUIPMENTS}/${companyId}`)

  return (
    <Container
      title={EQUIPMENT_TITLE}
      breadcrumbList={breadcrumbList}
      currentPageLabel={EQUIPMENT_DETAILS_BREADCRUMB}
      withNavigation={isOperator}
      loading={getEquipmentInfoRequest.loading || !isLoaded}
      rightBlock={
        <FormActionButtons
          handleBack={handleCancel}
          loading={updateEquipmentRequest.loading}
          formId={EQUIPMENT_FORM_ID}
        />
      }
    >
      <EquipmentDetailsForm
        handleSubmit={handleSubmit}
        initialValues={initValues}
        equipmentDetailsData={equipmentDetailsData}
        isEditing
      />
    </Container>
  )
}
